import { environment } from '../../environments/environment';
import { Observable, throwError } from 'rxjs';
import { Ship } from '../Ship';
import { HeaderService } from './header.service';
import { AuthService } from './auth.service';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ShipRoundTripHistory } from '../data/ShipRoundTripHistory';
import { Merchant } from '../Merchant';
import { ShipsContact } from '../data/ShipsContact';
import { BulkCardOrder } from '../data/BulkCardOrder';
import { ShipRoundTripInfo } from '../data/ShipRoundTripInfo';
import { Address } from '../data/Address';
import { ShipClassBulkCardOrderLimit } from '../data/ShipClassBulkCardOrderLimit';
import { ShipRosterIdentity } from '../data/ShipRosterIdentity';
import { catchError } from 'rxjs/operators';
import {ShipLog} from '../ships/ship-logs/ShipLog';
import { ShipInstallation } from '../data/ShipInstallation';
import {EomReportDetails} from '../data/EomReportDetails';
import { map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class ShipService {
  hasPendingSearch = false;
  newShipProfileSearchTerm = '';

  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private headerService: HeaderService
  ) {}

  getShips(): Observable<Ship[]> {
    const url = `${environment.rootApiUrl}/merchants/merchantsearch/getShips`;

    return this.http.get<Ship[]>(url, this.headerService.getHttpOptionsXAuthHeaderFromCookie());
  }

  getShipsCommon(): Observable<Ship[]> {
    const url = `${environment.rootApiUrl}/common/getShips`;

    return this.http.get<Ship[]>(url, this.headerService.getHttpOptionsXAuthHeaderFromCookie());
  }

  getShipRoundTripHistory(
    shipLocation: string
  ): Observable<ShipRoundTripHistory> {
    const url =
      `${environment.rootApiUrl}/ships/lastRoundTrips/` + shipLocation;

    return this.http.get<ShipRoundTripHistory>(url, this.headerService.getHttpOptionsXAuthHeaderFromCookie());
  }

  getLastSeen(
    shipLocation: string
  ): Observable<ShipInstallation[]> {
    const url =
      `${environment.rootApiUrl}/ships/lastSeen/` + shipLocation;

    return this.http.get<ShipInstallation[]>(url, this.headerService.getHttpOptionsXAuthHeaderFromCookie());
  }
  getLastEOM(
    shipLocation: string
  ): Observable<EomReportDetails[]> {
    const url =
      `${environment.rootApiUrl}/ships/lastEOM/` + shipLocation;
    return this.http.get<EomReportDetails[]>(url, this.headerService.getHttpOptionsXAuthHeaderFromCookie());
  }
  getShipProfiles(): Observable<Ship[]> {
    const url = `${environment.rootApiUrl}/ships/profiles`;

    return this.http.get<Ship[]>(url, this.headerService.getHttpOptionsXAuthHeaderFromCookie());
  }

  getMerchantsByShip(shipLocation: string): Observable<Merchant[]> {
    const url =
      `${environment.rootApiUrl}/ships/merchantsByShip/` + shipLocation;

    return this.http.get<Merchant[]>(url, this.headerService.getHttpOptionsXAuthHeaderFromCookie());
  }

  getMerchantsByShipClass(shipClass: string): Observable<Merchant[]> {
    const url = `${environment.rootApiUrl}/ships/class/` + shipClass;

    return this.http.get<Merchant[]>(url, this.headerService.getHttpOptionsXAuthHeaderFromCookie());
  }

  getLogsByShip(shipLocation: string): Observable<ShipLog[]> {
    const url = `${environment.rootApiUrl}/ships/logsByShip/` + shipLocation;

    return this.http.get<ShipLog[]>(url, this.headerService.getHttpOptionsXAuthHeaderFromCookie());
  }

  downloadShipLog(uuid: string) {
    return `${environment.rootApiUrl}/ships/downloadShipLog/` + uuid;
  }

  getShipContacts(shipLocation: string): Observable<ShipsContact> {
    const url = `${environment.rootApiUrl}/ships/shipsContact/` + shipLocation;

    return this.http.get<ShipsContact>(url, this.headerService.getHttpOptionsXAuthHeaderFromCookie());
  }

  getShipLookupCodes(lookupColumn: string): Observable<String[]> {
    const url = `${environment.rootApiUrl}/ships/shipLookupCodes/${lookupColumn}`;

    return this.http.get<String[]>(url, this.headerService.getHttpOptionsXAuthHeaderFromCookie());
  }

  getShipContactBlank(): Observable<ShipsContact> {
    const url = `${environment.rootApiUrl}/ships/shipsContactBlank`;

    return this.http.get<ShipsContact>(url, this.headerService.getHttpOptionsXAuthHeaderFromCookie());
  }

  getShipBulkCardOrders(shipLocation: string): Observable<BulkCardOrder[]> {
    const url =
      `${environment.rootApiUrl}/ships/bulkCardOrders/` + shipLocation;

    return this.http.get<BulkCardOrder[]>(url, this.headerService.getHttpOptionsXAuthHeaderFromCookie());
  }

  getShipClassBulkCardOrderLimits(): Observable<ShipClassBulkCardOrderLimit> {
    const url = `${environment.rootApiUrl}/ships/bulkCardOrders/limits`;

    return this.http.get<ShipClassBulkCardOrderLimit>(url, {
      headers: this.headerService.getXAuthHeaderFromCookie()
    });
  }

  activateBulkCordholder(shipLocation: string): Observable<BulkCardOrder[]> {
    const url =
      `${environment.rootApiUrl}/ships/profile/bulk_activate/` + shipLocation;

    return this.http.get<BulkCardOrder[]>(url, this.headerService.getHttpOptionsXAuthHeaderFromCookie());
  }

  orderReplacementCardsByLocation(shipLocation: string): Observable<any> {
    const url =
      `${environment.rootApiUrl}/ships/profile/orderReplacementByLocation/` + shipLocation;

    return this.http.get<any>(url, this.headerService.getHttpOptionsXAuthHeaderFromCookie());
  }

  searchShipProfiles(searchString: string): Observable<Ship[]> {
    const url = `${environment.rootApiUrl}/ships/profile/search`;
    let params = new HttpParams();
    params = params.append('shipUIC', searchString);

    return this.http.post<Ship[]>(url, params, this.headerService.getHttpOptionsXAuthHeaderFromCookie());
  }

  registerShip(ship: Ship): Observable<string> {
    const url = `${environment.rootApiUrl}/ships/registerNewShip`;
    let headers = this.headerService.getXAuthHeaderFromCookie();
    headers = headers.set('Content-Type', 'application/json');

    return this.http.post(url, ship, { headers: headers, responseType: 'text'});
  }

  getAllPendingShips(): Observable<ShipInstallation[]> {
    const url = `${environment.rootApiUrl}/ships/sync_setup`;
    let headers = this.headerService.getXAuthHeaderFromCookie();
    headers = headers.set('content-type', 'application/json');
    return this.http.get<ShipInstallation[]>(url, { headers: headers });
  }

  findPendingShip(hostName: string): Observable<Ship[]> {
    const url =
      `${environment.rootApiUrl}/ships/sync_setup/search?hostName=` + hostName;
    let headers = this.headerService.getXAuthHeaderFromCookie();
    headers = headers.set('content-type', 'application/json');
    return this.http.get<Ship[]>(url, { headers: headers });
  }

  pairShip(shipUuid: string, shipLocation: string): Observable<Ship[]> {
    const url =
      `${environment.rootApiUrl}/ships/sync_setup/update?shipUuid=` +
      shipUuid +
      `&shipLocation=` +
      shipLocation;
    let headers = this.headerService.getXAuthHeaderFromCookie();
    headers = headers.set('content-type', 'application/json');

    return this.http.post<any>(url, null, { headers: headers });
  }

  postShipRoundtripInfo(
    shipLocation: string,
    roundTripInfo: ShipRoundTripInfo
  ): Observable<ShipRoundTripInfo> {
    const url = `${environment.rootApiUrl}/ships/shipRoundTrip/` + shipLocation;
    let headers = this.headerService.getXAuthHeaderFromCookie();
    headers = headers.set('Content-Type', 'application/json');

    return this.http.post<any>(url, roundTripInfo, {headers: headers});
  }

  postSFANumber(shipLocation: string, sfaNumber: string): Observable<Ship> {
    const url = `${environment.rootApiUrl}/ships/updateMerchantSFA`;
    const params = new HttpParams()
      .append('shipLoc', shipLocation)
      .append('merSFA', sfaNumber);

    return this.http.post<any>(url, params, this.headerService.getHttpOptionsXAuthHeaderFromCookie());
  }

  postBulkCardOrders(
    shipLocation: string,
    bulkCardType: string,
    bulkCardQty: string
  ): Observable<BulkCardOrder[]> {
    const url =
      `${environment.rootApiUrl}/ships/profile/bulk_order_cards/` +
      shipLocation;

    const parms = new HttpParams()
      .append('bulkCardType', bulkCardType)
      .append('bulkCardQty', bulkCardQty);

    return this.http.post<BulkCardOrder[]>(url, parms, this.headerService.getHttpOptionsXAuthHeaderFromCookie());
  }

  postBulkCardCancel(
    shipLocation: string,
    type: string,
    dateCreatedFormatted: string,
    columnValue: string,
    batchUUID: string
  ): Observable<BulkCardOrder[]> {
    const url =
      `${environment.rootApiUrl}/ships/profile/bulk_cancel_order/` +
      shipLocation;
    const parms = new HttpParams()
      .append('type', type)
      .append('dateCreatedFormatted', dateCreatedFormatted)
      .append('pinMailerFlag', columnValue)
      .append('batchUUID', batchUUID);

    return this.http.post<BulkCardOrder[]>(url, parms, this.headerService.getHttpOptionsXAuthHeaderFromCookie());
  }

  postHullSwap(srcShip: string, desShip: string) {
    const url = `${environment.rootApiUrl}/ships/hull_swap/update`;
    const parms = new HttpParams()
      .append('srcShip', srcShip)
      .append('desShip', desShip);

    return this.http.post(url, parms, {
      headers: this.headerService.getXAuthHeaderFromCookie(),
      responseType: 'text'
    });
  }

  updateShipContact(
    shipLocation: string,
    disboName: string,
    disboEmail: string,
    disboPhone: string
  ): Observable<string> {
    const url = `${environment.rootApiUrl}/ships/updateShipsContact/updateDisboContact`;
    const params = new HttpParams()
      .append('UIC', shipLocation)
      .append('disboName', disboName)
      .append('disboEmail', disboEmail)
      .append('disboPhone', disboPhone);

    return this.http.post(url, params, {
      headers: this.headerService.getXAuthHeaderFromCookie(),
      responseType: 'text'
    });
  }

  updateShipLsrContact(
    shipLocation: string,
    lsrContact: string,
    lsrPhone: string
  ): Observable<any> {
    const url = `${environment.rootApiUrl}/ships/updateShipsContact/updateLSRContact`;
    const params = new HttpParams()
      .append('UIC', shipLocation)
      .append('lsrContact', lsrContact)
      .append('lsrPhone', lsrPhone);

    return this.http.post(url, params, {
      headers: this.headerService.getXAuthHeaderFromCookie(),
      responseType: 'text' as 'text'
    });
  }

  updateShipBulkAddress(
    shipLocation: String,
    address: Address
  ): Observable<any> {
    const url =
      `${environment.rootApiUrl}/ships/updateShipsContact/bulkAddress/` + shipLocation;

    let headers = this.headerService.getXAuthHeaderFromCookie();
    headers = headers.set('Content-Type', 'application/json');

    return this.http.post(url, address, {
      headers: headers,
      responseType: 'text' as 'text'
    });
  }

  updateEmvBulkAddress(
    shipLocation: String,
    address: Address
  ): Observable<any> {
    const url =
      `${environment.rootApiUrl}/ships/updateShipsContact/emvBulkAddress/` +
      shipLocation;
    let headers = this.headerService.getXAuthHeaderFromCookie();
    headers = headers.set('Content-Type', 'application/json');

    return this.http.post(url, address, {
      headers: headers,
      responseType: 'text' as 'text'
    });
  }

  storeShipProfile(ship: Ship) {
    sessionStorage.setItem('shipProfile', JSON.stringify(ship));
  }

  storeShipProfileResult(shipEntry: Ship) {
    // Get the list of ships
    const shipArray = this.retieveAllShipProfileResults();
    let uniqueEntry = true;

    // Loop through the array and make sure that this entry is unique
    shipArray.forEach((ship: Ship) => {
      if (
        shipEntry.shipName === ship.shipName &&
        shipEntry.shipHull === ship.shipHull &&
        shipEntry.shipLocation === ship.shipLocation &&
        shipEntry.disboName === ship.disboName &&
        shipEntry.disboPhone === ship.disboPhone &&
        shipEntry.disboEmail === ship.disboEmail
      ) {
        uniqueEntry = false;
      }
    });

    // If the entry is unique then take the last 4 from the array and add to bottom
    if (uniqueEntry) {
      const newArray = shipArray.slice(0).splice(-4);
      newArray.push(shipEntry);

      // Store the new array
      this.storeAllShipProfileResults(newArray);
    }
  }

  storeAllShipProfileResults(ships: Ship[]) {
    sessionStorage.setItem('shipProfileSearch', JSON.stringify(ships));
  }

  retrieveShipProfile(): Ship {
    return JSON.parse(sessionStorage.getItem('shipProfile')) as Ship;
  }

  retieveAllShipProfileResults(): Ship[] {
    const jsonString = sessionStorage.getItem('shipProfileSearch');
    if (jsonString === null || jsonString.trim().length === 0) {
      return new Array<Ship>();
    }

    return JSON.parse(jsonString) as Ship[];
  }

  setPendingSearch(newSearchTerm) {
    this.newShipProfileSearchTerm = newSearchTerm;
    this.hasPendingSearch = true;
  }

  getPendingSearch(): string | null {
    if (this.hasPendingSearch) {
      this.hasPendingSearch = false;
      return this.newShipProfileSearchTerm;
    } else {
      return null;
    }
  }

  insertAuditMessage(location: string) {
    const url = `${environment.rootApiUrl}/ships/shipPasswordAudit`;
    const parms = new HttpParams().append('shipLocation', location);

    return this.http.post(url, parms, {
      headers: this.headerService.getXAuthHeaderFromCookie(),
      responseType: 'text'
    });
  }

  getShipPasswords(location: string) {
    const url = `${environment.rootApiUrl}/ships/shipPassword`;
    const parms = new HttpParams().append('shipLocation', location);

    return this.http.post(url, parms, { headers: this.headerService.getXAuthHeaderFromCookie() });
  }

  getUnlock(location: string) {
    const url = `${environment.rootApiUrl}/ships/unlockStatus`;
    const parms = new HttpParams().append('shipLocation', location);

    return this.http.post(url, parms, { headers: this.headerService.getXAuthHeaderFromCookie() });
  }

  getShipRoster(location: string, firstParameter?: string, firstType?: string): Observable<ShipRosterIdentity[]> {
    const url = `${environment.rootApiUrl}/ships/shipRoster/${location}`;
    const params = new HttpParams()
      .append('firstParameter', firstParameter)
      .append('firstType', firstType);

    return this.http.get<ShipRosterIdentity[]>(url, { headers: this.headerService.getXAuthHeaderFromCookie(), params: params });
  }

  transfer(location: string, ids: Array<number>): Observable<Array<number>> {
    const url = `${environment.rootApiUrl}/ships/transferToShore/${location}`;
    let headers = this.headerService.getXAuthHeaderFromCookie();
    headers = headers.set('content-type', 'application/json');

    return this.http.post<Array<number>>(url, JSON.stringify(ids), { headers: headers }).pipe(
      catchError(error => {
        console.log(error.status);
        return throwError(error);
      })
    );
  }

  getShipBranches(location: string): Observable<string[]> {
    const url = `${environment.rootApiUrl}/ships/shipBranches/${location}`;

    return this.http.get<string[]>(url, { headers: this.headerService.getXAuthHeaderFromCookie() });
  }

  checkTransferForAO(ids: Array<number>): Observable<Array<Merchant>> {
    const url = `${environment.rootApiUrl}/ships/checkAccountableOfficial`;
    let headers = this.headerService.getXAuthHeaderFromCookie();
    headers = headers.set('content-type', 'application/json');

    return this.http.post<Array<Merchant>>(url, JSON.stringify(ids), { headers: headers }).pipe(
      catchError(error => {
        console.log(error.status);
        return throwError(error);
      })
    );
  }

  checkPermissionedUsers(): Observable<Map<string, object>> {
    const url = `${environment.rootApiUrl}/ships/permissionedUsers`;

    return this.http.get<Map<string, object>>(url, { headers: this.headerService.getXAuthHeaderFromCookie() }).pipe(
      map(responseObject => {
        return new Map(Object.entries(responseObject));
      })
    );
  }

  uploadCsv(shipUIC: string, fileData: string, fileName: string) {
    const url = `${environment.rootApiUrl}/ships/marineBulkOrder`;
    const fd = new FormData();
    fd.append('shipUIC', shipUIC);
    fd.append('file', fileData);
    fd.append('filename', fileName);
    const headers = this.headerService.getXAuthHeaderFromCookieNoType();
    return this.http.post(url, fd, { headers: headers, responseType: 'text' });
  }

  decryptCsv(file: File) {
    const url = `${environment.rootApiUrl}/ships/marineBulkOrderDecrypt`;
    const fd = new FormData();
    fd.append('file', file);
    const headers = this.headerService.getXAuthHeaderFromCookieNoType();
    return this.http.post(url, fd, { headers: headers, responseType: 'text' });
  }
}
